import { FC } from 'react';
import * as React from 'react';
import cn from 'classnames';
import { Content } from '@raydiant/api-client-js';
import Text from '@raydiant/raydial/components/Text';
import { Check, User } from '@raydiant/raydial/components/Icons';
import { capitalize } from '../../../../../utilities/stringUtils';
import { LibraryPageModalType } from '../../../../../pages/LibraryPage/v2/LibraryPageV2';
import Spinner from '@raydiant/raydial/components/Spinner';
import ApplicationIcon from '../ApplicationIcon';
import { getPresentationData } from '../../../utils';
import SidebarMenuTooltip from '../../../../../components/Sidebar/SidebarMenuTooltip';
import ContentCardActionButtonGroup from './ContentCardActionButtonGroup';

interface ContentResourceCardProps {
  content: Content;
  showOwner?: boolean;
  selected?: boolean;
  batchMode?: boolean;
  onClick: (content: Content) => void;
  onSelect: (content: Content) => void;
  onButtonGroupClick: (contentId: string) => void;
  onButtonGroupItemClick: (modalType: LibraryPageModalType) => void;
  onClose: () => void;
}

const LibraryContentCard: FC<ContentResourceCardProps> = (
  {
    content,
    showOwner = false,
    selected = false,
    batchMode = false,
    onClick,
    onSelect,
    onButtonGroupClick,
    onButtonGroupItemClick,
    onClose,
  },
  ref,
) => {
  const nextContent = {
    ...content,
    presentationData: getPresentationData(content),
  };

  const isPresentation = content.resourceType === 'presentation';
  const fileUploads = content.presentationData?.fileUploads || [];
  const hasFileUploads = fileUploads.length > 0;
  const isFileUploading =
    isPresentation &&
    hasFileUploads &&
    !fileUploads.every((fileUpload) =>
      fileUpload?.completedSteps?.some(
        (step) => step.stepName === 'upload_completed',
      ),
    );

  const renderShowOwnerInfo = () => {
    return (
      <SidebarMenuTooltip
        placement="bottom"
        title={`Owner: ${capitalize(content.ownedBy.name)}`}
        arrow
      >
        <div
          className="bg-white w-6 h-6 rounded-xl flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <User size={15} className="mb-0.5" />
        </div>
      </SidebarMenuTooltip>
    );
  };

  const renderSelectStatus = (content: Content) => {
    return (
      <div
        className="bg-white w-6 h-6 rounded-xl flex items-center justify-center"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(content);
        }}
      >
        {selected && <Check className="text-blue-600" />}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'h-[185px] rounded-[20px] shadow border-2 border-grey-100 flex-col justify-start items-start inline-flex cursor-pointer',
        selected && 'border-blue-600',
      )}
      onClick={() => onClick(content)}
    >
      {/* The ref should be applied on the thumbnail for correct drag and drop calculations */}
      <div
        className="relative w-[220px] h-[125px] p-4 rounded-t-[17px] justify-between flex bg-center bg-no-repeat bg-cover"
        ref={ref}
      >
        <div
          ref={ref}
          className="absolute inset-0 rounded-t-[17px] bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url(${
              nextContent.presentationData.thumbnailUrl ||
              content.presentationData.applicationThumbnailUrl
            })`,
          }}
        />
        <div className="z-[2]">
          {(batchMode || selected) && renderSelectStatus(content)}
          {!batchMode && showOwner && renderShowOwnerInfo()}
        </div>
        <div className="z-[2]">
          {isFileUploading ? (
            <Spinner />
          ) : (
            <ContentCardActionButtonGroup
              content={content}
              onButtonGroupClick={onButtonGroupClick}
              onButtonGroupItemClick={onButtonGroupItemClick}
              onButtonGroupClose={onClose}
            />
          )}
        </div>
      </div>
      <div className="w-[220px] h-[60px] px-4 bg-white rounded-b-[20px] flex-col justify-center items-start gap-2.5 flex">
        <div className="self-stretch justify-start items-center gap-2 inline-flex">
          <ApplicationIcon
            url={nextContent.presentationData.applicationIconUrl}
          />
          <div className="grow shrink basis-0 text-gray-600 break-all line-clamp-2">
            <Text variant="body">{nextContent.name}</Text>
          </div>
        </div>
      </div>
    </div>
  );
};

// @ts-ignore
export default React.forwardRef(LibraryContentCard);
